// src/components/Dashboard/ClaimDetailCard.jsx
// Fila de UN cheque para la página de detalle completo de un reclamo
// (/claim-status/claim/:groupId, ver ClaimDetailPage.jsx). Solo muestra
// datos propios del cheque (monto, %, fecha, respaldo, closing statement,
// acciones) — los datos compartidos del reclamo (cliente, empresa,
// ajustador, aseguradora, causa...) van en el header de la página, no
// repetidos por cheque. Layout en fila (id/fecha/estado — monto — stats —
// acciones) según el mockup aprobado por el usuario.
import React, { useContext, useState } from 'react';
import { LanguageContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import MoreMenu from '../common/MoreMenu';
import { viewInvoice } from '../../utils/invoiceGenerator';
import { openBackup } from '../../services/backupService';
import { getClosingStatementById } from '../../services/closingStatementService';
import { formatDateToMMDDYYYY } from '../../utils/dateHelpers';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};

const ClaimDetailCard = ({ claim, index, isAdmin = false, onContinue = null, onDelete = null }) => {
  const { language } = useContext(LanguageContext);
  const { can } = useAuth();
  // Lo que gana CSR (su %, el pendiente, el estado de pago, el invoice) solo lo ven Super Admin y P.A Admin.
  const showEarnings = can('viewEarnings');
  const [error, setError] = useState('');
  const storedBackup = Boolean(claim?.checkAttachment?.storageKey);
  const adjusterGross = ((claim.amount || 0) * (claim.adjusterPercentage || 0)) / 100;
  const csrAmount = claim.totalCollected || 0;
  const pendingPct = claim.amount ? ((claim.pending || 0) / claim.amount) * 100 : 0;
  // Captura/comprobante subido al registrar cada pago (ver CheckPayment.jsx
  // — "Captura del pago (opcional)"), no el monto en sí — eso ya lo cubren
  // Ajustador/CSR/Pendiente arriba. Puede haber más de un pago parcial.
  const paymentReceipts = (claim.paymentHistory || []).filter(p => p.backup?.storageKey);

  const handleOpenStoredCheck = async () => {
    try {
      await openBackup(claim.checkAttachment.storageKey);
    } catch (err) {
      console.error('Error opening stored check backup:', err);
      setError(language === 'es' ? 'No se pudo abrir el respaldo del cheque.' : 'Could not open the check backup.');
    }
  };

  const handleOpenPaymentReceipt = async (storageKey) => {
    try {
      await openBackup(storageKey);
    } catch (err) {
      console.error('Error opening payment receipt:', err);
      setError(language === 'es' ? 'No se pudo abrir el comprobante de pago.' : 'Could not open the payment receipt.');
    }
  };

  const handleOpenClosingStatementPdf = async () => {
    try {
      const statement = await getClosingStatementById(claim.closingStatementId);
      if (!statement?.signedPdf?.storageKey) {
        setError(language === 'es' ? 'Este Closing Statement todavía no tiene un PDF firmado subido.' : 'This Closing Statement has no signed PDF uploaded yet.');
        return;
      }
      await openBackup(statement.signedPdf.storageKey);
    } catch (err) {
      console.error('Error opening closing statement PDF:', err);
      setError(language === 'es' ? 'No se pudo abrir el Closing Statement.' : 'Could not open the Closing Statement.');
    }
  };

  const es = language === 'es';
  const menuItems = [
    storedBackup && { label: es ? 'Ver cheque' : 'View check', icon: 'fa-eye', onSelect: handleOpenStoredCheck },
    claim.closingStatementId && { label: 'Closing Statement', icon: 'fa-file-lines', onSelect: handleOpenClosingStatementPdf },
    // Los cheques que ya tienen un sobre de firma lo guardaron como docusignEnvelopeUrl (claimService.js).
    (claim.signatureUrl || claim.docusignEnvelopeUrl) && { label: es ? 'Documento a firmar' : 'Document to sign', icon: 'fa-signature', onSelect: () => window.open(claim.signatureUrl || claim.docusignEnvelopeUrl, '_blank', 'noopener,noreferrer') },
    ...(showEarnings ? paymentReceipts : []).map((payment, i) => ({
      label: `${es ? 'Comprobante de pago' : 'Payment receipt'}${paymentReceipts.length > 1 ? ` ${i + 1}` : ''}`,
      icon: 'fa-receipt',
      onSelect: () => handleOpenPaymentReceipt(payment.backup.storageKey),
    })),
    showEarnings && can('downloadInvoice') && { label: 'Invoice', icon: 'fa-file-invoice', onSelect: () => viewInvoice(claim) },
    isAdmin && onDelete && { label: es ? 'Eliminar cheque' : 'Delete check', icon: 'fa-trash', danger: true, onSelect: onDelete },
  ];

  return (
    <div className="cdd-check-card">
      <div className="cdd-check-top">
        <div className="cdd-check-id">
          <div className="cdd-check-id-row">
            <span className="cdd-check-badge">
              {language === 'es' ? 'Cheque' : 'Check'} {String(index + 1).padStart(2, '0')}
            </span>
            {showEarnings && (
              <span className={`cd-status-badge ${claim.paid ? 'cd-status-paid' : 'cd-status-pending'}`}>
                {claim.paid ? (language === 'es' ? 'Pagado' : 'Paid') : (language === 'es' ? 'Pendiente' : 'Pending')}
              </span>
            )}
          </div>
          {showEarnings && claim.invoiceNumber && <span className="cdd-check-invoice">Invoice #{claim.invoiceNumber}</span>}
          <span className="cdd-check-date">
            <i className="fa-regular fa-calendar" aria-hidden="true"></i> {formatDateToMMDDYYYY(claim.checkDate)}
          </span>
        </div>

        <div className="cdd-check-amount">
          <span className="cdd-stat-label">{language === 'es' ? 'Monto del cheque' : 'Check amount'}</span>
          <strong className="cdd-stat-value cdd-stat-value-lg">{formatCurrency(claim.amount)}</strong>
        </div>
      </div>

      {(showEarnings || claim.adjusterPercentage !== undefined) && <div className="cdd-check-stats">
        {claim.adjusterPercentage !== undefined && <div className="cdd-stat">
          <div className="cdd-stat-head">
            <span className="cdd-stat-label">{language === 'es' ? 'Ajustador' : 'Adjuster'}</span>
            <span className="cdd-stat-pct">{claim.adjusterPercentage || 0}%</span>
          </div>
          <strong className="cdd-stat-value">{formatCurrency(adjusterGross)}</strong>
        </div>}
        {showEarnings && <div className="cdd-stat">
          <div className="cdd-stat-head">
            <span className="cdd-stat-label">CSR</span>
            <span className="cdd-stat-pct">{claim.csrPercentage || 0}%</span>
          </div>
          <strong className="cdd-stat-value">{formatCurrency(csrAmount)}</strong>
        </div>}
        {showEarnings && <div className="cdd-stat">
          <div className="cdd-stat-head">
            <span className="cdd-stat-label">{language === 'es' ? 'Pendiente' : 'Pending'}</span>
            <span className="cdd-stat-pct">{pendingPct.toFixed(0)}%</span>
          </div>
          <strong className="cdd-stat-value">{formatCurrency(claim.pending)}</strong>
        </div>}
      </div>}

      {claim.payableTo && (
        <div className="cdd-check-payto">
          <span className="cdd-stat-label">{language === 'es' ? 'Pagable a' : 'Payable to'}</span>
          <span className="cdd-check-payto-value">{claim.payableTo}</span>
        </div>
      )}

      {error && (
        <div className="cd-error cdd-check-error" role="alert">
          <i className="fa-solid fa-exclamation-triangle" aria-hidden="true"></i> {error}
        </div>
      )}

      <div className="cdd-check-actions">
        {/* Acciones de escritura, portadas de MyClaims.jsx al fusionar My
            Claims con Claim Status 2026-09-20 — admin-only, saltan al
            wizard de Create (único lugar que sabe editar un cheque) en vez
            de duplicar esa lógica acá. Es la acción primaria de la fila. */}
        {isAdmin && onContinue && (
          <button type="button" onClick={onContinue} className="cd-btn cd-btn-check">
            <i className="fa-solid fa-arrow-right" aria-hidden="true"></i> {claim.archived ? (language === 'es' ? 'Ver' : 'View') : (language === 'es' ? 'Continuar' : 'Continue')}
          </button>
        )}
        {/* Documentos y acciones secundarias agrupados; Eliminar va al final
            y abre la confirmación existente (onDelete). */}
        <MoreMenu label={language === 'es' ? 'Documentos y más acciones' : 'Documents and more actions'} items={menuItems} />
      </div>
    </div>
  );
};

export default ClaimDetailCard;
